import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import { rectDeflate, type Insets, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext, TextSpan } from "../render/RenderContext.js";
import type { DrawableTexture } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export interface DialogueLine {
  /** Who is talking. Omitted for narration. */
  speaker?: string;
  /** Plain text, or spans to color a name or an item inside the line. */
  text: string | readonly TextSpan[];
  /** A portrait drawn beside the text. */
  portrait?: DrawableTexture | null;
  /** Sub-rectangle of the portrait texture, for a sheet of expressions. */
  portraitSource?: Rect;
  /** Color of the speaker's name. */
  speakerColor?: Color | null;
  /** Characters a second for this line. Overrides the box's rate. */
  charactersPerSecond?: number;
}

export interface DialogueBoxOptions extends ControlOptions {
  line?: DialogueLine | null;
  /** Reveal rate. 0 shows the whole line at once. */
  charactersPerSecond?: number;
  padding?: Insets;
  /** Side of the square portrait. */
  portraitSize?: number;
  font?: FontSpec | null;
  speakerFont?: FontSpec | null;
  /** Blinking marker once the line is fully revealed. */
  showContinueCaret?: boolean;
  onAdvance?: () => void;
}

const DEFAULT_CPS = 45;
const CARET_BLINK_S = 0.6;
const PORTRAIT_GAP = 10;

/**
 * The visual-novel text box: a portrait, a speaker, and a line that types
 * itself out.
 *
 * The two-stage click is the whole interaction and is worth being exact
 * about. A click part-way through the reveal **completes the line** rather
 * than advancing past it; only a click on a finished line advances. Getting
 * that backwards means an impatient reader skips half the script by
 * double-clicking, which is the classic bug in a hand-rolled one of these.
 *
 * The reveal is measured in characters rather than in wrapped lines, so
 * resizing the box mid-line re-wraps without losing the reader's place.
 */
export class DialogueBox extends Control {
  #line: DialogueLine | null;
  /** Frame time the current line started revealing. */
  #startedAt = -1;
  /** Characters shown, or -1 while it is still being timed out. */
  #revealed = -1;
  #animating = false;
  /** The body wrapped for one reveal count, width and font; the caret blink repaints it unchanged. */
  #wrapped: {
    line: DialogueLine;
    revealed: number;
    width: number;
    font: FontSpec;
    size: number;
    lines: TextSpan[][];
  } | null = null;

  charactersPerSecond: number;
  padding: Insets;
  portraitSize: number;
  font: FontSpec | null;
  speakerFont: FontSpec | null;
  showContinueCaret: boolean;

  constructor(options: DialogueBoxOptions = {}) {
    super({ width: 560, height: 140, tabStop: true, cursor: "pointer", ...options });
    this.#line = options.line ?? null;
    this.charactersPerSecond = options.charactersPerSecond ?? DEFAULT_CPS;
    this.padding = options.padding ?? { left: 16, top: 12, right: 16, bottom: 12 };
    this.portraitSize = options.portraitSize ?? 88;
    this.font = options.font ?? null;
    this.speakerFont = options.speakerFont ?? null;
    this.showContinueCaret = options.showContinueCaret ?? true;
    if (options.onAdvance) this.on("itemactivate", () => options.onAdvance!());

    this.on("click", () => this.advance());
    this.on("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      this.advance();
      e.handled = true;
    });
    if (this.#line) this.setLine(this.#line);
  }

  get line(): DialogueLine | null {
    return this.#line;
  }

  /** Show a new line and start it typing. */
  setLine(line: DialogueLine | null): void {
    this.#line = line;
    this.#revealed = -1;
    this.#startedAt = this.desktop?.frameTime ?? 0;
    if (!line || this.#rate(line) <= 0) this.#revealed = this.#fullLength();
    this.#syncAnimation();
    this.emit("change");
    this.invalidate();
  }

  /** The whole line's text, styling flattened away. */
  get plainText(): string {
    const line = this.#line;
    if (!line) return "";
    return typeof line.text === "string" ? line.text : line.text.map((s) => s.text).join("");
  }

  /** How much of the line has been typed out so far. */
  get revealedCount(): number {
    return this.#revealed < 0 ? 0 : Math.min(this.#revealed, this.#fullLength());
  }

  /** True once the whole line is on screen. */
  get complete(): boolean {
    return this.#revealed >= this.#fullLength();
  }

  /** Show the rest of the line at once. */
  finishLine(): void {
    if (this.complete) return;
    this.#revealed = this.#fullLength();
    this.#syncAnimation();
    this.invalidate();
  }

  /**
   * What a click means: finish the line if it is still typing, otherwise ask
   * for the next one. Returns true when it advanced.
   */
  advance(): boolean {
    if (!this.complete) {
      this.finishLine();
      return false;
    }
    this.emit("itemactivate", 0);
    return true;
  }

  #rate(line: DialogueLine): number {
    return line.charactersPerSecond ?? this.charactersPerSecond;
  }

  #fullLength(): number {
    return this.plainText.length;
  }

  #advanceReveal(now: number): void {
    const line = this.#line;
    if (!line || this.complete) return;
    const rate = this.#rate(line);
    if (rate <= 0) {
      this.#revealed = this.#fullLength();
      return;
    }
    // Derived from the start time rather than accumulated per frame, so a
    // dropped frame does not slow the line down.
    this.#revealed = Math.floor((now - this.#startedAt) * rate);
  }

  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    // Frames while typing, and while the caret is blinking afterwards.
    const want = !this.complete || (this.showContinueCaret && this.#line !== null);
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  protected override onEnvironmentChanged(): void {
    this.#animating = false;
    this.#syncAnimation();
  }

  /** The area the line's text is laid out in, past the portrait. */
  textRect(): Rect {
    const inner = rectDeflate(this.localRect, this.padding);
    if (!this.#line?.portrait) return inner;
    const shift = this.portraitSize + PORTRAIT_GAP;
    return { x: inner.x + shift, y: inner.y, width: Math.max(0, inner.width - shift), height: inner.height };
  }

  /**
   * The visible part of the line, as spans.
   *
   * Cut in character terms across the whole line rather than per span, so a
   * reveal that stops mid-word inside a colored run keeps that run's color
   * for the part already shown.
   */
  visibleSpans(): TextSpan[] {
    const line = this.#line;
    if (!line) return [];
    const spans = typeof line.text === "string" ? [{ text: line.text }] : line.text;
    const limit = this.revealedCount;
    if (limit >= this.#fullLength()) return [...spans];

    const out: TextSpan[] = [];
    let seen = 0;
    for (const span of spans) {
      if (seen >= limit) break;
      const take = Math.min(span.text.length, limit - seen);
      out.push(take === span.text.length ? span : { ...span, text: span.text.slice(0, take) });
      seen += take;
    }
    return out;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const now = this.desktop?.frameTime ?? 0;
    this.#advanceReveal(now);
    this.#syncAnimation();

    const p = theme.palette;
    const r = this.localRect;
    theme.drawDialogueBox(rc, r);

    const line = this.#line;
    if (!line) return;
    const inner = rectDeflate(r, this.padding);

    if (line.portrait) {
      const box: Rect = {
        x: inner.x,
        y: inner.y,
        width: this.portraitSize,
        height: Math.min(this.portraitSize, inner.height),
      };
      rc.drawTexture(line.portrait, box, line.portraitSource);
    }

    const text = this.textRect();
    const speakerFont = this.speakerFont ?? theme.metrics.captionFont;
    const font = this.font ?? theme.metrics.font;
    let y = text.y;

    if (line.speaker) {
      rc.drawText(line.speaker, text.x, y, speakerFont, line.speakerColor ?? p.accent, {
        baseline: "top",
        maxWidth: text.width,
      });
      y += rc.lineHeight(speakerFont);
    }

    const body: Rect = { x: text.x, y, width: text.width, height: Math.max(0, text.y + text.height - y) };
    const revealed = this.revealedCount;
    const c = this.#wrapped;
    let lines: TextSpan[][];
    if (
      c &&
      c.line === line &&
      c.revealed === revealed &&
      c.width === body.width &&
      c.font === font &&
      c.size === font.size
    ) {
      lines = c.lines;
    } else {
      lines = rc.wrapSpans(this.visibleSpans(), font, body.width);
      this.#wrapped = { line, revealed, width: body.width, font, size: font.size, lines };
    }
    rc.drawSpanBlock(lines, body, font, p.text, { lineHeight: rc.lineHeight(font) });

    if (!this.showContinueCaret || !this.complete) return;
    // A blinking triangle in the corner: the universal "press to go on".
    const on = (now % (CARET_BLINK_S * 2)) < CARET_BLINK_S;
    if (!on) return;
    const size = 8;
    rc.fillTriangle(
      { x: r.width - this.padding.right - size, y: r.height - this.padding.bottom - size, width: size, height: size },
      "down",
      p.accent,
    );
  }
}
